import { useRef, useState } from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { ArrowLeft, Check, User } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { BrandCheckCard } from "./components/BrandCheckCard"
import { useTextareaFlags } from "@/hooks/use-flag-highlights"

// About the Author: the coach's bio and photo for the back of the book. The
// photo is the coach's account headshot unless they upload one for this book.
export default function BookAbout({ currentUser, book, about, brand }) {
  const form = useForm({ about_author: about.bio || "", author_photo: null })
  const [preview, setPreview] = useState(about.photoUrl || about.accountPhotoUrl)
  const changed = form.data.about_author !== (about.bio || "") || form.data.author_photo
  const locked = book.sentToPrint
  // "Show in your bio" selects the Brand check's flagged words in the bio.
  const bio = useRef(null)
  const locate = useTextareaFlags(bio, form.data.about_author, brand.flags)

  const save = (event) => {
    event.preventDefault()
    form.transform((data) => ({ _method: "patch", book: data }))
    form.post(`/app/coach/books/${book.id}`, { forceFormData: true, preserveScroll: true, onSuccess: () => form.setData("author_photo", null) })
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`About the Author - ${book.title}`} />
      <div className="max-w-3xl space-y-5">
        <div className="flex items-start gap-3">
          <Link href={`/app/coach/books/${book.id}`} className="rounded-md p-2 hover:bg-zinc-100" aria-label="Back to the book">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-kf-red">{book.title}</div>
            <h1 className="text-2xl font-bold text-zinc-900">About the Author</h1>
            <p className="text-zinc-600">Your bio and photo for the back of the book.</p>
          </div>
          {about.done && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><Check className="h-4 w-4" /> Done</span>}
        </div>

        <Card>
          <CardContent>
            <form onSubmit={save} className="grid gap-6 sm:grid-cols-[140px_1fr]">
              <div className="space-y-2">
                <div className="flex h-44 w-36 items-center justify-center overflow-hidden rounded bg-zinc-100">
                  {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <User className="h-8 w-8 text-zinc-400" />}
                </div>
                {!locked && (
                  <label className="block cursor-pointer text-sm font-medium text-kf-red">
                    Use a different photo
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        form.setData("author_photo", file)
                        setPreview(URL.createObjectURL(file))
                      }} />
                  </label>
                )}
                <p className="text-xs text-zinc-500">
                  {about.photoUrl ? "A photo for this book only." : about.accountPhotoUrl ? "Your account photo." : "Add a photo."}
                </p>
                {form.errors.author_photo && <p className="text-xs text-red-600">{form.errors.author_photo}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">Your bio</Label>
                <Textarea ref={bio} id="bio" rows={12} value={form.data.about_author} disabled={locked}
                  onChange={(e) => form.setData("about_author", e.target.value)}
                  placeholder="Write about yourself in the third person: your work, your family, your faith, and why you coach Christian business leaders." />
                <p className="text-xs text-zinc-500">The AI also reads this when it drafts your author letter. It says nothing about you that is not here.</p>
                {!locked && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button type="submit" disabled={!changed || form.processing}>Save</Button>
                    <Button type="button" variant="outline" disabled={changed || about.done || !about.bio || !preview}
                      onClick={() => router.post(`/app/coach/books/${book.id}/about_done`)}>
                      <Check className="h-4 w-4" /> Mark as done
                    </Button>
                  </div>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
        <BrandCheckCard bookId={book.id} brand={brand} locked={locked} locate={locate} showText="Show in your bio"
          notCheckedText="Runs when you run the Brand check from My Book, and again when you assemble the book."
          fixText="Or change your bio above and save." />
      </div>
    </CoachLayout>
  )
}
